import {useNotification} from '@hconnect/uikit/src/common'
import {useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {mutations} from '../../../../../api/mutations'
import {invalidateSettingsQuery} from '../../../../common/providers/ApiInterface'
import {notifyIfErrorMessage} from '../../../../common/utils/errorHandling'
import {
  AssetStandardOperationTime,
  AssetStandardOperationTimeConflictResolutionAction,
  AssetStandardOperationTimeValidationError,
  NewAssetStandardOperationTimeData
} from '../../../types'
import {generateICalString} from '../../../utils'

type AddStandardOperationTimeParams = {
  onSuccess: () => void
}

export const useAddStandardOperationTime = ({onSuccess}: AddStandardOperationTimeParams) => {
  const {t} = useTranslation()
  const {notify} = useNotification()
  const raiseError = useErrorHandler()

  const {mutate, isPending} = useMutation<
    AssetStandardOperationTime,
    AxiosError<AssetStandardOperationTimeValidationError>,
    Parameters<typeof mutations.addStandardOperationTime>[0]
  >({
    mutationFn: mutations.addStandardOperationTime,
    onError: (error) => {
      notifyIfErrorMessage(error.response?.data.detail, raiseError, notify)
    },
    onSuccess: () => {
      notify('success', t('success.addStandardOperationTime'))
      onSuccess && onSuccess()
    },
    onSettled: () => {
      invalidateSettingsQuery('assetsStandardOperationTimes')
    }
  })

  const mutateFn = useCallback(
    (
      data: {
        plantCode: string
        plantTimeZone: string
        assetId: number
        onConflict: AssetStandardOperationTimeConflictResolutionAction
      } & NewAssetStandardOperationTimeData,
      {onError}: {onError: (error: AxiosError<AssetStandardOperationTimeValidationError>) => void}
    ) =>
      mutate(
        {
          onConflict: data.onConflict,
          plantCode: data.plantCode,
          AssetStandardOperationTimeDto: {
            calendarEvent: generateICalString(data.calendarEvent, data.plantTimeZone),
            assetId: data.assetId,
            isFixed: data.isFixed,
            operationModeId: data.operationModeId
          }
        },
        {onError}
      ),
    [mutate]
  )

  return {
    isPending,
    mutate: mutateFn
  }
}
